/* FILE: ./frontend/src/features/canada/components/SearchFilters.jsx */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useTranslation } from 'react-i18next'; 

const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

const CONSTRUCTION_CHAPTERS = ['25', '27', '32', '39', '44', '68', '69', '70', '72', '73', '74', '76', '83'];

// THE SYNONYM DICTIONARY (Fast Fallback)
const TRADE_SYNONYMS = [
    { keywords: ['beam', 'h-beam', 'i-beam', 'structural steel', 'girder'], code: '7216.32', description: 'Structural Steel H/I/U Beams & Sections' },
    { keywords: ['pipe', 'tube', 'tubing', 'hollow'], code: '7304.00', description: 'Steel Pipes, Tubes and Hollow Profiles' },
    { keywords: ['lumber', 'wood', 'timber', 'board', 'plywood'], code: '4407.11', description: 'Sawn Wood, Lumber & Timber (Coniferous)' },
    { keywords: ['wire', 'cable', 'electrical'], code: '8544.11', description: 'Insulated Copper Wire & Electrical Cables' },
    { keywords: ['cement', 'concrete', 'mortar'], code: '2523.29', description: 'Portland Cement & Hydraulic Cements' },
    { keywords: ['drywall', 'gypsum', 'plasterboard'], code: '6809.11', description: 'Plasterboards & Drywall Panels' }
];

const MASTERFORMAT_CATEGORIES = [
  'Concrete & Cementitious',
  'Masonry & Stone',
  'Structural Steel & Heavy Metal',
  'Cold-Formed Metal Framing',
  'Mass Timber & Engineered Wood',
  'Rough Carpentry & Lumber',
  'Thermal & Moisture Protection',
  'Exterior Enclosure & Cladding',
  'Fenestration',
  'Earth Retention & Piling'
];

const CAPABILITY_TAGS = {
  'Engineering & Pre-Construction': [
    'BIM / VDC Modeling', 'In-House Structural Engineering', 'Shop Drawing Detailing', 'Design-Assist'
  ],
  'Manufacturing & Fabrication': [
    'Custom Structural Fabrication', 'CNC Machining & Cutting', 'Roll Forming & Extrusion', 'Metal Stamping & Punching', 'Pre-Assembly / Modular Construction'
  ],
  'Welding & Treatment': [
    'CWB Certified Welding', 'Robotic Welding', 'TIG / MIG / Arc Welding', 'Hot-Dip Galvanizing', 'Powder Coating & Painting', 'Anodizing', 'Sandblasting / Surface Prep'
  ],
  'Logistics & Site': [
    'Field Erection / Site Installation', 'Just-In-Time (JIT) Delivery', 'Oversize / Heavy Haul Transport', 'Crane & Rigging Services'
  ]
};

export default function SearchFilters({ onSearch, isLoading = false }) {
  const { t } = useTranslation(); 
  const { user, userProfile, isSupplier } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeHsCode, setActiveHsCode] = useState(''); 
  const [province, setProvince] = useState('');
  const [showMyListings, setShowMyListings] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [showFilters, setShowFilters] = useState(false); 

  const [validationError, setValidationError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchingDb, setIsSearchingDb] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // ⚡ The Lock: Prevents ghost dropdowns from opening automatically
  const [isSelectionLocked, setIsSelectionLocked] = useState(false);

  const validateTradeCode = (term) => {
      if (!term) return '';
      const numericSearch = term.replace(/,/g, '.').replace(/[^0-9.]/g, '');
      if (numericSearch.length >= 2) {
          const chapter = numericSearch.substring(0, 2);
          const unifiedError = "Platform Focus: TariffBeater is restricted exclusively to Core & Shell and Building Envelope materials. This item is outside our scope.";
          
          if (chapter === '27') {
              const allowed27 = ['2713', '2714', '2715'];
              if (!allowed27.some(code => numericSearch.startsWith(code))) return unifiedError;
          } else if (!CONSTRUCTION_CHAPTERS.includes(chapter)) {
              return unifiedError;
          } else {
              const BLOCKED_PREFIXES = [
                  '3923', '3924', '4401', '4402', '4419', '4420', '6812',
                  '6911', '6912', '6913', '7010', '7013', '7018', '7321',
                  '7323', '7324', '7418', '7615', '8304', '8306'
              ];
              if (BLOCKED_PREFIXES.some(prefix => numericSearch.startsWith(prefix))) {
                  return unifiedError;
              }
          }
      }
      return '';
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    const urlProvince = params.get('province') || ''; 
    const urlCategory = params.get('category') || '';
    const urlCapabilities = params.get('capabilities') ? params.get('capabilities').split(',') : [];

    let defaultProvince = 'ON';
    if (userProfile && userProfile.province) {
        defaultProvince = userProfile.province;
    }
    
    const targetProvince = urlProvince || defaultProvince;

    if (urlSearch || urlProvince || urlCategory || urlCapabilities.length > 0) {
        
        // ⚡ THE FIX: Do not overwrite the beautiful human-readable string if it already contains the target code!
        const isVisualMatch = searchTerm && urlSearch && searchTerm.includes(`(${urlSearch})`);
        
        if (!isVisualMatch && urlSearch !== activeHsCode) {
            // ⚡ Fetch description for raw URL loads to make them pretty!
            const fetchDesc = async () => {
                const { data } = await supabase.from('hs_codes').select('description').eq('code', urlSearch).maybeSingle();
                if (data) {
                    setIsSelectionLocked(true);
                    setSearchTerm(`${data.description} (${urlSearch})`);
                } else {
                    setSearchTerm(urlSearch);
                }
                setActiveHsCode(urlSearch); 
            };
            fetchDesc();
        }
        
        setProvince(targetProvince); 
        setSelectedCategory(urlCategory);
        setSelectedCapabilities(urlCapabilities);

        const errorMsg = validateTradeCode(urlSearch);
        if (errorMsg) {
            setValidationError(errorMsg);
            return; 
        }

        onSearch({ 
            search: urlSearch, 
            province: targetProvince, 
            category: urlCategory,
            capabilities: urlCapabilities.join(','),
            showMyListings: false 
        });
        return; 
    }

    setProvince(defaultProvince);
  }, [location.search, userProfile]); // eslint-disable-line

  useEffect(() => {
    if (isSelectionLocked) {
        setShowDropdown(false);
        return;
    }

    if (!searchTerm || searchTerm.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
    }

    const fetchSuggestions = async () => {
        setIsSearchingDb(true);
        try {
            const queryLower = searchTerm.toLowerCase();
            let allMatches = [];

            // 1. Check Hardcoded Synonyms (Fast fallback)
            TRADE_SYNONYMS.forEach(item => {
                if (item.keywords.some(kw => queryLower.includes(kw))) {
                    allMatches.push({ code: item.code, description: item.description });
                }
            });

            // 2. Query live 'hs_synonyms' Supabase table
            const { data: dbSynonyms } = await supabase
                .from('hs_synonyms')
                .select('colloquial_term, target_hs_code')
                .ilike('colloquial_term', `%${queryLower}%`)
                .limit(5);

            if (dbSynonyms) {
                dbSynonyms.forEach(s => {
                    allMatches.push({ code: s.target_hs_code, description: s.colloquial_term });
                });
            }

            // 3. Query official HS Database
            let query = supabase.from('hs_codes').select('code, description');
            
            const numericSearch = searchTerm.replace(/,/g, '.').replace(/[^0-9.]/g, '');            
            if (numericSearch.length >= 2) {
                query = query.ilike('code', `${numericSearch}%`);
            } else {
                const words = searchTerm.trim().split(/\s+/).filter(w => w.length > 2);
                const chapterFilter = CONSTRUCTION_CHAPTERS.map(ch => `code.ilike.${ch}%`).join(',');
                query = query.or(chapterFilter);
                words.forEach(w => {
                    query = query.ilike('description', `%${w}%`);
                });
            }

            const { data: officialCodes, error } = await query.limit(5);
            if (error) throw error;
            
            // 4. Merge all sources and remove duplicates
            const combined = [...allMatches, ...(officialCodes || [])];
            const unique = Array.from(new Map(combined.map(item => [item.code, item])).values());

            setSuggestions(unique.slice(0, 6));
            // Only open if focused
            if (document.activeElement?.id === 'search') {
                setShowDropdown(unique.length > 0);
            }

        } catch (err) {
            console.error("Autocomplete Error:", err);
        } finally {
            setIsSearchingDb(false);
        }
    };

    const delayDebounceFn = setTimeout(() => { fetchSuggestions(); }, 300); 
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, isSelectionLocked]);

  useEffect(() => {
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const logAudit = (actionType, status, errorMsg = null, additionalParams = {}) => {
      if (!user?.id) return;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      fetch(`${apiUrl}/audit/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
          body: JSON.stringify({
              action_type: actionType,
              status: status,
              error_message: errorMsg,
              parameters: additionalParams
          })
      }).catch(() => {});
  };

  const handleSearch = () => {
    setShowDropdown(false);
    
    const payloadSearch = activeHsCode || searchTerm;

    const errorMsg = validateTradeCode(payloadSearch);
    if (errorMsg) {
        setValidationError(errorMsg);
        return; 
    }

    setValidationError(''); 

    logAudit('DIRECTORY_SEARCH', 'SUCCESS', null, { 
        search_term: payloadSearch, 
        category: selectedCategory,
        capabilities: selectedCapabilities,
        destination_province: province, 
        my_listings_only: showMyListings 
    });

    onSearch({ 
        search: payloadSearch, 
        province, 
        category: selectedCategory,
        capabilities: selectedCapabilities.join(','),
        showMyListings 
    });
  };

  const handleSuggestionClick = (item) => {
      // ⚡ THE FIX: Set the visual string, completely wipe suggestions, and lock dropdown
      setIsSelectionLocked(true);
      setSearchTerm(`${item.description} (${item.code})`);
      setActiveHsCode(item.code);
      setSuggestions([]); 
      setShowDropdown(false);
      
      const errorMsg = validateTradeCode(item.code);
      if (errorMsg) {
          setValidationError(errorMsg);
          return;
      }
      
      setValidationError(''); 
      
      logAudit('DIRECTORY_SEARCH', 'SUCCESS', null, { 
          search_term: item.code, 
          category: selectedCategory,
          capabilities: selectedCapabilities,
          destination_province: province, 
          my_listings_only: showMyListings 
      });

      onSearch({ 
          search: item.code, 
          province, 
          category: selectedCategory,
          capabilities: selectedCapabilities.join(','),
          showMyListings 
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const toggleCapability = (cap) => {
    setSelectedCapabilities(prev => 
        prev.includes(cap) ? prev.filter(c => c !== cap) : [...prev, cap]
    );
  };

  const clearSearch = () => {
    setSearchTerm('');
    setActiveHsCode('');
    setSelectedCategory('');
    setSelectedCapabilities([]);
    setValidationError('');
    setSuggestions([]);
    setIsSelectionLocked(false);
    const defaultProv = userProfile?.province || 'ON';
    setProvince(defaultProv);
    setShowMyListings(false);
    onSearch({ search: '', category: '', capabilities: '', province: defaultProv, showMyListings: false });
  };

  return (
    <div
      className="professional-card mb-8 border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border"
      data-testid="search-filters"
    >
      <div className="p-6">
        
        {location.search.includes('search=') && !province && (
            <div className="mb-6 bg-blue-100 text-blue-800 p-4 rounded-lg text-sm font-bold animate-pulse border border-blue-200 flex items-center shadow-sm">
                <span className="text-xl mr-3">👋</span> 
                {t('search_filters.welcome')}
            </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('search_filters.title')}
            </h2>
            <p className="text-gray-600">
              {t('search_filters.desc')}
            </p>
          </div>
          <div className="hidden lg:flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-4 items-start w-full">
          
          <div className="md:col-span-4 relative flex flex-col w-full" ref={dropdownRef}>
            <label
              htmlFor="search"
              className="form-label flex items-center space-x-2 mb-1 text-sm font-bold text-gray-700 h-6"
            >
              <span>{t('search_filters.search_label')}</span>
            </label>
            <div className={`relative transition-all duration-200 ${isFocused ? 'transform scale-[1.02]' : ''}`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <input
                id="search"
                type="text"
                autoComplete="off"
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setActiveHsCode(''); 
                    setValidationError('');
                    setIsSelectionLocked(false);
                }}
                onFocus={() => { setIsFocused(true); if (suggestions.length > 0 && !isSelectionLocked) setShowDropdown(true); }}
                onBlur={() => setIsFocused(false)}
                onKeyPress={handleKeyPress}
                placeholder={t('search_filters.ph_search')}
                className={`w-full rounded-lg shadow-sm focus:ring-blue-500 p-2.5 pl-10 pr-10 border text-sm bg-white ${validationError ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setActiveHsCode(''); setShowDropdown(false); setSuggestions([]); setValidationError(''); setIsSelectionLocked(false); }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {showDropdown && (
                <div className="absolute top-[70px] z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-xl overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                    {isSearchingDb ? (
                         <div className="p-3 text-sm text-gray-50 flex items-center gap-2">
                             <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                             {t('search_filters.searching_db')}
                         </div>
                    ) : (
                        <ul>
                            {suggestions.map((item, index) => (
                                <li 
                                    key={index}
                                    onClick={() => handleSuggestionClick(item)}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 flex items-start gap-3 transition-colors"
                                >
                                    <span className="text-blue-600 mt-0.5">🔍</span>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 line-clamp-1">{item.description}</p>
                                        <p className="text-xs font-mono text-blue-700 mt-0.5">{t('search_filters.hs_code')} {item.code}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
          </div>

          <div className="md:col-span-3 flex flex-col w-full">
            <label className="form-label flex items-center space-x-2 mb-1 text-sm font-bold text-gray-700 h-6">
              <span>Category</span>
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2.5 border text-sm bg-white transition-colors"
            >
              <option value="">-- All Categories --</option>
              {MASTERFORMAT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3 flex flex-col w-full">
            <label
              htmlFor="province"
              className="form-label flex items-center space-x-2 mb-1 text-sm font-bold text-gray-700 h-6"
            >
              <span>{t('search_filters.dest_label')}</span>
              <span className="text-red-500">*</span>
            </label>
            <select
              id="province"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className={`w-full rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2.5 border text-sm transition-colors ${!province ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'}`}
            >
              <option value="">{t('search_filters.select_dest')}</option>
              {CANADIAN_PROVINCES.map((prov) => (
                <option key={prov.code} value={prov.code}>
                  {prov.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 flex flex-col w-full mt-[28px]">
            <div className="flex gap-2">
                <button
                onClick={handleSearch}
                disabled={isLoading || !province}
                className={`flex-1 w-full py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                    isLoading || !province
                    ? 'bg-blue-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm transform hover:scale-[1.02]'
                }`}
                >
                {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                    <span>{t('search_filters.find_btn')}</span>
                )}
                </button>
                
                {(searchTerm || selectedCategory || selectedCapabilities.length > 0 || province) && (
                    <button 
                        onClick={clearSearch} 
                        className="inline-flex items-center px-3 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shrink-0"
                    >
                        {t('search_filters.clear')}
                    </button>
                )}
            </div>
          </div>
        </div>

        {/* ⚡ RESPECTFUL VALIDATION ERROR UI */}
        {validationError && (
          <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg flex items-start space-x-3 shadow-sm animate-fade-in-up">
            <span className="text-xl">🏛️</span>
            <div>
              <p className="text-amber-900 font-bold text-sm tracking-wide">{validationError}</p>
            </div>
          </div>
        )}

        {/* ⚡ CAPABILITIES TOGGLE BAR */}
        <div className="mt-6 border-t border-blue-200/50 pt-4">
            <div className="flex justify-between items-center mb-4">
                <button 
                    onClick={() => setShowFilters(!showFilters)}
                    className="text-sm font-bold text-blue-700 flex items-center gap-2 hover:text-blue-900 transition-colors"
                >
                    <svg className={`w-4 h-4 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    Advanced Capabilities & Services
                    {selectedCapabilities.length > 0 && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                            {selectedCapabilities.length} Selected
                        </span>
                    )}
                </button>
                
                {user && isSupplier && (
                    <label className="inline-flex items-center cursor-pointer select-none">
                        <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        checked={showMyListings}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setShowMyListings(checked);
                            onSearch({ search: activeHsCode || searchTerm, category: selectedCategory, capabilities: selectedCapabilities.join(','), province, showMyListings: checked });
                        }}
                        />
                        <span className="ml-2 text-sm text-gray-700 font-medium">{t('search_filters.show_my_listings')}</span>
                    </label>
                )}
            </div>

            {showFilters && (
                <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-inner grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in-up">
                    {Object.entries(CAPABILITY_TAGS).map(([groupName, tags]) => (
                        <div key={groupName}>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{groupName}</h4>
                            <div className="flex flex-wrap gap-2">
                                {tags.map(tag => {
                                    const isSelected = selectedCapabilities.includes(tag);
                                    return (
                                        <button
                                            key={tag}
                                            onClick={() => toggleCapability(tag)}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${
                                                isSelected 
                                                ? 'bg-blue-100 border-blue-500 text-blue-800 font-bold shadow-sm' 
                                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-white'
                                            }`}
                                        >
                                            {isSelected && <span className="mr-1">✓</span>}
                                            {tag}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

      </div>
    </div>
  );
}

SearchFilters.propTypes = {
  onSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};